import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from "react";
import { useBookshelf } from "./BookshelfContext";
import { getRoomFileUrl } from "../lib/roomFileStorage";
import {
  CATEGORIES,
  colorForType,
  labelForType,
  toPlainText,
  toSafeHtml,
  filterDocuments,
  scoreMatch,
  bookIdForDeskDoc,
  deskDocIdForBook,
  hasSeededRoomBookshelf,
  markRoomBookshelfSeeded,
  sendHighlightBooks,
  sendAddBook,
  sendRemoveBook,
  sendBookEditMode,
  sendOpenBooks,
  unsyncedBooks,
} from "../lib/bookshelf";
import {
  setUnityBookClickedHandler,
  setUnityBookAddedHandler,
  setUnityBookAddFailedHandler,
  setUnityBooksReorderedHandler,
} from "../lib/unityEvents";

const INK       = "#2E2A24";
const INK_MUTED = "#7A736A";
const CARD_BG   = "#F4F1EA";
const CARD_LINE = "rgba(0,0,0,0.09)";
const TEAL      = "#4DB8B5";
const DANGER    = "#D9534F";

const CLICK_POINT_TTL_MS = 1500;
const SEARCH_DEBOUNCE_MS = 160;

const CARD_PREVIEW_CLAMP  = "line-clamp-4";
const CARD_LINE_HEIGHT_PX = 19;

const CARD_W  = 324;
const MODAL_W = 780;

const SHELF_HALF_WIDTH_RATIO = 0.22;
const SHELF_GAP = 20;

const FRAME_TOP     = 74;
const HEADER_GAP    = 20;
const SEARCH_BOTTOM = 44;
const FRAME_W      = 700;
const SEARCH_W     = 525;

const GLASS = {
  background: "rgba(255,255,255,0.78)",
  backdropFilter: "blur(14px)",
  WebkitBackdropFilter: "blur(14px)",
  border: "1px solid rgba(0,0,0,0.10)",
  boxShadow: "0 2px 10px rgba(0,0,0,0.07)",
};

const HEADER_GLASS = {
  ...GLASS,
  background: "transparent",
};

const ICON_PATHS = {
  document: (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </>
  ),
  note: (
    <>
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <line x1="8" y1="9" x2="16" y2="9" />
      <line x1="8" y1="13" x2="16" y2="13" />
      <line x1="8" y1="17" x2="12" y2="17" />
    </>
  ),
  reference: <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />,
  whiteboard: <path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />,
  upload: (
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  ),
  preview: (
    <>
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  send: (
    <>
      <polyline points="15 14 20 9 15 4" />
      <path d="M4 20v-7a4 4 0 0 1 4-4h12" />
    </>
  ),
  archive: (
    <>
      <rect x="3" y="4" width="18" height="4" rx="1" />
      <path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8" />
      <line x1="10" y1="13" x2="14" y2="13" />
    </>
  ),
  download: (
    <>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </>
  ),
  trash: (
    <>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </>
  ),
};

function Icon({ name, size = 14 }) {
  const path = ICON_PATHS[name];
  if (!path) return null;
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"
      className="flex-shrink-0"
      aria-hidden="true"
    >
      {path}
    </svg>
  );
}

function formatEdited(ts) {
  if (!ts) return "";
  return `Edited ${new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
}

function summarize(doc) {
  const text = toPlainText(doc?.content);
  if (text) return text.replace(/\s+/g, " ").trim().slice(0, 120);
  if (doc?.fileName) return doc.fileName;
  return "No preview available.";
}

function TypeBadge({ type }) {
  const color = colorForType(type);
  return (
    <span
      className="inline-flex items-center px-[9px] py-[3px] rounded-full text-[11px] font-semibold whitespace-nowrap"
      style={{ color, background: `${color}22` }}
    >
      {labelForType(type)}
    </span>
  );
}

function ActionButton({ icon, label, onClick, disabled, reason, danger, inline }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={disabled ? reason : label}
      className={
        inline
          ? "flex flex-row items-center gap-[9px] bg-transparent border-0 px-1 py-1 rounded-md whitespace-nowrap"
          : "flex flex-col items-center gap-[4px] bg-transparent border-0 px-0 py-1 rounded-md flex-1 min-w-0"
      }
      style={{
        color: danger ? DANGER : INK_MUTED,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.4 : 1,
      }}
    >
      <Icon name={icon} size={inline ? 18 : 16} />
      <span className={inline ? "text-[13px] font-medium" : "text-[11px] font-medium leading-tight text-center"}>
        {label}
      </span>
    </button>
  );
}

function ActionRow({ doc, targets, onSend, onArchive, onDownload, onDelete, onPreview, inline }) {
  const [sendOpen, setSendOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!sendOpen) return;
    const close = (e) => { if (!wrapRef.current?.contains(e.target)) setSendOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [sendOpen]);

  return (
    <div
      className={inline ? "flex items-center justify-between pt-[14px]" : "flex items-start pt-[12px]"}
      style={{ borderTop: `1px solid ${CARD_LINE}` }}
    >
      <div
        className={inline ? "flex items-center" : "flex items-start flex-1 min-w-0"}
        style={{ gap: inline ? 26 : 2 }}
      >
        {onPreview && (
          <div className={inline ? "relative" : "relative flex-1 min-w-0 flex"}>
            <ActionButton icon="preview" label="Preview" inline={inline} onClick={onPreview} />
          </div>
        )}
        <div className={inline ? "relative" : "relative flex-1 min-w-0 flex"} ref={wrapRef}>
          <ActionButton
            icon="send"
            label={inline ? "Send to Hotspot" : "Send"}
            inline={inline}
            onClick={() => setSendOpen((o) => !o)}
          />
          {sendOpen && (
            <div
              className="absolute left-0 bottom-full mb-2 rounded-lg py-1 z-10 min-w-[200px]"
              style={{ background: "#FFF", boxShadow: "0 8px 28px rgba(0,0,0,0.18)", border: `1px solid ${CARD_LINE}` }}
            >
              {targets.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  disabled={!t.enabled}
                  title={t.enabled ? "" : t.reason}
                  onClick={() => { setSendOpen(false); onSend(t.id); }}
                  className="w-full text-left px-3 py-[7px] bg-transparent border-0 text-[12px]"
                  style={{
                    color: t.enabled ? INK : INK_MUTED,
                    cursor: t.enabled ? "pointer" : "not-allowed",
                    opacity: t.enabled ? 1 : 0.55,
                  }}
                >
                  {t.label}
                  {!t.enabled && <div className="text-[10px] leading-tight mt-[1px]">{t.reason}</div>}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className={inline ? "pl-[26px]" : "contents"} style={inline ? { borderLeft: `1px solid ${CARD_LINE}` } : undefined}>
          <ActionButton
            icon="archive"
            label={doc.status === "archived" ? "Restore" : "Archive"}
            inline={inline}
            onClick={onArchive}
          />
        </div>
        <div className={inline ? "pl-[26px]" : "contents"} style={inline ? { borderLeft: `1px solid ${CARD_LINE}` } : undefined}>
          <ActionButton
            icon="download"
            label="Download"
            inline={inline}
            onClick={onDownload}
            disabled={!doc.storagePath && !toPlainText(doc.content)}
            reason="Nothing to download yet."
          />
        </div>
      </div>
      <div
        className={inline ? "flex items-center pl-[26px] flex-shrink-0" : "flex items-start pl-[8px] ml-[6px] flex-shrink-0"}
        style={{ borderLeft: `1px solid ${CARD_LINE}` }}
      >
        <ActionButton icon="trash" label="Delete" inline={inline} onClick={onDelete} danger />
      </div>
    </div>
  );
}

// ── Category tabs ────────────────────────────────────────────
function CategoryTabs({ active, onToggle, showArchived, onToggleArchived, archivedCount }) {
  return (
    <div className="flex items-center justify-center gap-[10px] flex-wrap pointer-events-auto">
      {CATEGORIES.map((cat) => {
        const on = active.includes(cat.id);
        return (
          <button
            key={cat.id}
            type="button"
            onClick={() => onToggle(cat.id)}
            className="flex items-center gap-[8px] px-[13px] py-[8px] rounded-[10px] text-[13px] font-semibold"
            style={{
              ...GLASS,
              color: INK,
              border: `1px solid ${on ? cat.color : "rgba(0,0,0,0.10)"}`,
              boxShadow: on
                ? `inset 0 0 0 1px ${cat.color}, 0 0 6px ${cat.color}CC, 0 0 16px 3px ${cat.color}59, ${GLASS.boxShadow}`
                : GLASS.boxShadow,
              cursor: "pointer",
            }}
          >
            <Icon name={cat.id} />
            {cat.label}
            <span className="w-[7px] h-[7px] rounded-full flex-shrink-0" style={{ background: cat.color }} />
          </button>
        );
      })}
      {(showArchived || archivedCount > 0) && (
        <button
          type="button"
          onClick={onToggleArchived}
          title="Archived documents stay off the shelf until you restore them"
          className="flex items-center gap-[8px] px-[13px] py-[8px] rounded-[10px] text-[13px] font-semibold"
          style={{
            ...GLASS,
            color: showArchived ? INK : INK_MUTED,
            border: `1px solid ${showArchived ? INK : "rgba(0,0,0,0.10)"}`,
            boxShadow: showArchived
              ? `inset 0 0 0 1px ${INK}, 0 0 6px ${INK}99, ${GLASS.boxShadow}`
              : GLASS.boxShadow,
            cursor: "pointer",
          }}
        >
          <Icon name="archive" />
          Archived{archivedCount > 0 ? ` (${archivedCount})` : ""}
        </button>
      )}
    </div>
  );
}

// ── Starter books ────────────────────────────────────────────
const STARTER_BOOKS = [
  {
    id: "seed-bookshelf-guide",
    title: "Guide to the Bookshelf",
    type: "document",
    status: "final",
    tags: ["guide", "bookshelf", "help"],
    content: "Welcome to your Bookshelf.\n\nEvery document Lumen creates at your Desk, every idea from your Whiteboard - it all lives here as a book.\n\nBrowse manually, or ask Lumen to find something. Relevant books glow when you search.\n\nYour Master Doc always lives in the glass case at center. The shelf is your room's long-term memory.",
  },
  {
    id: "seed-neuroquest-history",
    title: "The Wonderful History of NeuroQuest",
    type: "reference",
    status: "final",
    tags: ["neuroquest", "story", "origin"],
    content: "It started with a maddening observation: every productivity tool ever built assumes your brain works in straight lines.\n\nADHD brains don't work that way. They work spatially. They remember WHERE something was, not WHAT it was called.\n\nNeuroQuest was born from that observation. Built on the Method of Loci - an ancient memory technique over 2,500 years old. Spatial memory as productivity infrastructure.",
  },
  {
    id: "seed-a-day-in-adhd",
    title: "A Day in ADHD",
    type: "note",
    status: "final",
    tags: ["adhd", "guide", "understanding"],
    content: "6:47am. Wake up with three fully-formed ideas.\n\n9:00am. Notice a notification. Forty-five minutes later, you've researched something you didn't need to know.\n\n10:30am. The task feels impossible to start. Not because it's hard. Because the moment has passed.\n\nThis is task initiation failure. It's not laziness. It's neurology.\n\nYou're not broken. The tools were.",
  },
];

const STARTER_ID_PREFIX = "seed-";

const seededRooms = new Set();

// ── Card preview ─────────────────────────────────────────────
function CardPreview({ html, text, onReadMore }) {
  const bodyRef = useRef(null);
  const [truncated, setTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    setTruncated(el.scrollHeight > el.clientHeight + 1);
  }, [html, text]);

  const bodyClass = `text-[12.5px] ${CARD_PREVIEW_CLAMP}`;
  const bodyStyle = { color: INK, lineHeight: `${CARD_LINE_HEIGHT_PX}px` };

  return (
    <div className="mb-[12px]">
      {html ? (
        <div
          ref={bodyRef}
          className={`${bodyClass} ${CARD_PROSE}`}
          style={bodyStyle}
          dangerouslySetInnerHTML={{ __html: html }}
        />
      ) : (
        <div ref={bodyRef} className={bodyClass} style={bodyStyle}>
          {text}
        </div>
      )}

      {truncated && (
        <button
          type="button"
          onClick={onReadMore}
          className="mt-[4px] text-[12px] font-semibold bg-transparent border-0 p-0 cursor-pointer"
          style={{ color: TEAL }}
        >
          Read More
        </button>
      )}
    </div>
  );
}

// ── Click card ───────────────────────────────────────────────
function BookCard({ doc, point, onClose, onPreview, onToggleReference, rightInset = 0, ...actions }) {
  const cardRef = useRef(null);
  const previewHtml = useMemo(() => toSafeHtml(doc?.content), [doc?.content]);
  const [height, setHeight] = useState(0);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    if (cardRef.current) setHeight(cardRef.current.offsetHeight);
  });

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const centerX = vw / 2;
  const shelfEdge = SHELF_HALF_WIDTH_RATIO * vw;

  // Lumen and My Day live in a fixed stack on the right. Treat that strip as off
  // limits so the card never opens underneath them - if the right side is taken, the
  // card flips to the left of the shelf regardless of where the book was clicked.
  const rightBound = vw - Math.max(rightInset, 12);
  const rightStart = centerX + shelfEdge + SHELF_GAP;
  const leftStart  = centerX - shelfEdge - SHELF_GAP - CARD_W;
  const rightFits  = rightStart + CARD_W <= rightBound;
  const leftFits   = leftStart >= 12;

  const onRight = rightFits && (point.x >= centerX || !leftFits);
  const left = onRight
    ? Math.min(rightStart, rightBound - CARD_W)
    : Math.max(Math.min(leftStart, rightBound - CARD_W), 12);

  const top = height
    ? Math.min(Math.max(point.y - height / 2, 12), Math.max(12, vh - height - 12))
    : point.y;

  const lineEndX = onRight ? left : left + CARD_W;
  const isReference = doc.type === "reference";

  return (
    <>
      <svg
        className="fixed inset-0 pointer-events-none"
        width={vw} height={vh}
        style={{ zIndex: 59, overflow: "visible" }}
      >
        <line
          x1={point.x} y1={point.y} x2={lineEndX} y2={point.y}
          stroke="rgba(0,0,0,0.28)" strokeWidth="3.5" strokeLinecap="round"
        />
        <line
          x1={point.x} y1={point.y} x2={lineEndX} y2={point.y}
          stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round"
        />
        <circle cx={point.x} cy={point.y} r="4.5" fill="rgba(0,0,0,0.28)" />
        <circle cx={point.x} cy={point.y} r="3" fill="#FFFFFF" />
        <circle cx={lineEndX} cy={point.y} r="4.5" fill="rgba(0,0,0,0.28)" />
        <circle cx={lineEndX} cy={point.y} r="3" fill="#FFFFFF" />
      </svg>

      <div
        ref={cardRef}
        className="fixed rounded-[14px] p-[15px] pointer-events-auto"
        style={{
          left, top, width: CARD_W, zIndex: 60,
          background: CARD_BG,
          boxShadow: "0 16px 44px rgba(0,0,0,0.3)",
        }}
      >
        <div className="flex items-start justify-between gap-2 mb-[7px]">
          <div className="text-[15px] font-bold leading-tight" style={{ color: INK }}>{doc.title}</div>
          <button
            type="button" onClick={onClose} aria-label="Close"
            className="bg-transparent border-0 cursor-pointer text-[15px] leading-none flex-shrink-0"
            style={{ color: INK_MUTED }}
          >
            ✕
          </button>
        </div>

        <div className="flex items-baseline gap-[8px] mb-[6px]">
          <TypeBadge type={doc.type} />
        </div>

        <div className="text-[11px] mb-[8px]" style={{ color: INK_MUTED }}>{formatEdited(doc.updatedAt)}</div>
        <CardPreview
          html={previewHtml}
          text={previewHtml ? null : summarize(doc)}
          onReadMore={onPreview}
        />

        <label className="flex items-center gap-[8px] mb-[11px] cursor-pointer select-none">
          <input type="checkbox" checked={isReference} onChange={onToggleReference} />
          <span className="text-[11.5px]" style={{ color: INK_MUTED }}>Keep as reference</span>
        </label>

        <ActionRow doc={doc} onPreview={onPreview} {...actions} />
      </div>
    </>
  );
}

// ── Full document panel ──────────────────────────────────────
const CARD_PROSE = [
  "[&_h1]:text-[13.5px] [&_h1]:font-bold [&_h1]:mt-[9px] [&_h1]:mb-[3px]",
  "[&_h2]:text-[13px] [&_h2]:font-bold [&_h2]:mt-[8px] [&_h2]:mb-[3px]",
  "[&_h3]:text-[12.5px] [&_h3]:font-semibold [&_h3]:mt-[7px] [&_h3]:mb-[2px]",
  "[&_p]:mb-[5px]",
  "[&_ul]:list-disc [&_ul]:pl-[16px] [&_ul]:mb-[5px]",
  "[&_ol]:list-decimal [&_ol]:pl-[16px] [&_ol]:mb-[5px]",
  "[&_li]:mb-[2px]",
  "[&_strong]:font-bold [&_b]:font-bold [&_em]:italic [&_i]:italic",
  "[&_blockquote]:border-l-2 [&_blockquote]:border-black/15 [&_blockquote]:pl-[8px] [&_blockquote]:italic",
  "[&_a]:underline",
  "[&_hr]:my-[7px] [&_hr]:border-black/10",
  "[&>*:first-child]:mt-0",
].join(" ");

const DOC_PROSE = [
  "[&_h1]:text-[19px] [&_h1]:font-bold [&_h1]:mt-[20px] [&_h1]:mb-[8px]",
  "[&_h2]:text-[16px] [&_h2]:font-bold [&_h2]:mt-[18px] [&_h2]:mb-[7px]",
  "[&_h3]:text-[14.5px] [&_h3]:font-semibold [&_h3]:mt-[15px] [&_h3]:mb-[6px]",
  "[&_p]:mb-[10px]",
  "[&_ul]:list-disc [&_ul]:pl-[22px] [&_ul]:mb-[10px]",
  "[&_ol]:list-decimal [&_ol]:pl-[22px] [&_ol]:mb-[10px]",
  "[&_li]:mb-[4px]",
  "[&_strong]:font-bold [&_b]:font-bold [&_em]:italic [&_i]:italic",
  "[&_blockquote]:border-l-2 [&_blockquote]:border-black/15 [&_blockquote]:pl-[12px] [&_blockquote]:italic [&_blockquote]:my-[10px]",
  "[&_a]:underline",
  "[&_hr]:my-[16px] [&_hr]:border-black/10",
  "[&>*:first-child]:mt-0",
].join(" ");

function DocumentModal({ doc, onClose, ...actions }) {
  const html = useMemo(() => toSafeHtml(doc?.content), [doc?.content]);

  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-5 pointer-events-auto"
      style={{ zIndex: 70 }}
      onClick={onClose}
    >
      <div
        className="w-full rounded-[16px] flex flex-col overflow-hidden"
        style={{
          maxWidth: MODAL_W, maxHeight: "78vh", background: CARD_BG,
          boxShadow: "0 24px 70px rgba(0,0,0,0.38)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-[26px] pt-[22px] pb-[14px]">
          <div className="min-w-0">
            <div className="flex items-center gap-[10px] flex-wrap">
              <h2 className="text-[21px] font-bold m-0 leading-tight" style={{ color: INK }}>{doc.title}</h2>
              <TypeBadge type={doc.type} />
            </div>
            <div className="text-[12.5px] mt-[4px]" style={{ color: INK_MUTED }}>{formatEdited(doc.updatedAt)}</div>
          </div>
          <button
            type="button" onClick={onClose} aria-label="Close"
            className="bg-transparent border-0 cursor-pointer text-[19px] leading-none flex-shrink-0"
            style={{ color: INK_MUTED }}
          >
            ✕
          </button>
        </div>

        <div
          className="flex-1 overflow-y-auto px-[26px] pt-[16px] pb-[6px]"
          style={{ borderTop: `1px solid ${CARD_LINE}` }}
        >
          {html ? (
            <div
              className={`text-[14px] leading-[1.7] ${DOC_PROSE}`}
              style={{ color: INK }}
              dangerouslySetInnerHTML={{ __html: html }}
            />
          ) : (
            <div className="text-[13px] italic py-6" style={{ color: INK_MUTED }}>
              {doc.storagePath
                ? `${doc.fileName || "This file"} is stored as an attachment. Download it to open.`
                : "This document is empty."}
            </div>
          )}
        </div>

        <div className="px-[26px] pb-[18px] pt-[6px]">
          <ActionRow doc={doc} inline {...actions} />
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// MAIN FRAME
// ═══════════════════════════════════════════════════════════
export default function BookshelfPanel({
  onClose,
  onOpenInHotspot,
  deskDocIds = [],
  rightInset = 0,
  bottomInset = 0,
  roomId,
  userId,
  onDeskDocDeleted,
}) {
  const {
    listDocuments, deleteDocument, updateDocument, uploadFileToRoom,
    saveToBookshelf, storageError, loaded,
  } = useBookshelf();

  const [activeCategories, setActiveCategories] = useState([]);
  const [query, setQuery]                   = useState("");
  const [submittedQuery, setSubmittedQuery] = useState("");
  const [showArchived, setShowArchived]     = useState(false);
  const [selectedId, setSelectedId]         = useState(null);
  const [previewId, setPreviewId]           = useState(null);
  const [clickPoint, setClickPoint]         = useState(null);
  const [uploading, setUploading]           = useState(false);
  const [banner, setBanner]                 = useState("");
  const [editMode, setEditMode]             = useState(false);
  const fileInputRef = useRef(null);

  const editModeRef = useRef(editMode);
  useEffect(() => { editModeRef.current = editMode; }, [editMode]);

  const unplacedRef = useRef(new Set());

  const lastClickRef = useRef(null);
  useEffect(() => {
    const record = (e) => { lastClickRef.current = { x: e.clientX, y: e.clientY, t: Date.now() }; };
    window.addEventListener("mousedown", record, true);
    return () => window.removeEventListener("mousedown", record, true);
  }, []);

  useEffect(() => {
    if (!loaded || !userId || !roomId) return;
    const key = `${userId}:${roomId}`;
    if (seededRooms.has(key)) return;
    seededRooms.add(key);

    if (hasSeededRoomBookshelf(userId, roomId)) return;
    markRoomBookshelfSeeded(userId, roomId);

    const roomBooks = listDocuments({ roomId });
    const alreadySeeded = roomBooks.some((d) =>
      String(d.id).startsWith(STARTER_ID_PREFIX)
    );
    if (process.env.NODE_ENV === "development") {
      console.info(
        `[Bookshelf] seeding room ${roomId}: ${roomBooks.length} doc(s) here, starters ` +
        (alreadySeeded ? "already present" : "being added")
      );
    }
    if (alreadySeeded) return;

    STARTER_BOOKS.forEach((book) =>
      saveToBookshelf({
        ...book,
        // Per-room copies need per-room ids: id is the primary key alongside user_id.
        id: `${book.id}-${roomId}`,
        sourceHotspot: "desk",
        roomId,
      })
    );
  }, [loaded, userId, roomId]); // eslint-disable-line react-hooks/exhaustive-deps

  const roomDocs     = listDocuments({ roomId });

  const roomDocsRef = useRef(roomDocs);
  useEffect(() => { roomDocsRef.current = roomDocs; }, [roomDocs]);
  const shelfDocs    = useMemo(() => roomDocs.filter((d) => d.status !== "archived"), [roomDocs]);
  const archivedDocs = useMemo(() => roomDocs.filter((d) => d.status === "archived"), [roomDocs]);

  const sourceDocs  = showArchived ? archivedDocs : shelfDocs;
  const visibleDocs = useMemo(
    () => filterDocuments(sourceDocs, { categories: activeCategories, query: submittedQuery }),
    [sourceDocs, activeCategories, submittedQuery]
  );

  const selectedDoc = roomDocs.find((d) => d.id === selectedId) || null;
  const previewDoc  = roomDocs.find((d) => d.id === previewId) || null;

  const filtering = activeCategories.length > 0 || !!submittedQuery;

  useEffect(() => {
    if (!loaded) return;
    const pending = unsyncedBooks(shelfDocs);
    if (process.env.NODE_ENV === "development") {
      console.info(
        `[Bookshelf] shelf has ${shelfDocs.length} doc(s), sending ${pending.length} to Unity`,
        shelfDocs.map((d) => `${d.id} "${d.title}"`)
      );
    }
    pending.forEach((doc) => sendAddBook(doc));
  }, [loaded, shelfDocs]);

  const highlight = useMemo(() => {
    if (!filtering) return [];
    return visibleDocs
      .map((d) => ({
        id: d.id,
        strength: submittedQuery ? scoreMatch(d, submittedQuery) : 1,
      }))
      .filter((h) => h.strength > 0);
  }, [filtering, visibleDocs, submittedQuery]);

  useEffect(() => {
    if (!loaded) return;
    sendHighlightBooks(highlight);
  }, [loaded, highlight]);

  const openBook = useCallback((bookId) => {
    if (!roomDocsRef.current.some((d) => d.id === bookId)) {
      console.warn(
        `[Bookshelf] Unity sent book id "${bookId}" but no document matches it. ` +
        "Unity's ids come from world_json/surface_items; the panel reads bookshelf_documents."
      );
      return;
    }
    const point = lastClickRef.current;
    const fresh = point && Date.now() - point.t < CLICK_POINT_TTL_MS;
    setClickPoint(fresh ? point : { x: window.innerWidth / 2, y: window.innerHeight / 2 });
    setSelectedId(bookId);
    setPreviewId(null);
  }, []);

  useEffect(() => {
    setUnityBookClickedHandler((bookId) => openBook(bookId));
    setUnityBookAddedHandler((bookId, slot) => {
      unplacedRef.current.delete(bookId);
      if (Number.isInteger(slot)) updateDocument(bookId, { slot });
    });
    setUnityBookAddFailedHandler((bookId, reason) => {
      unplacedRef.current.add(bookId);
      setBanner(
        reason === "full"
          ? "This shelf is full. Archive a book to make room and this one will appear."
          : "Couldn't place that book on the shelf."
      );
    });
    setUnityBooksReorderedHandler((books) => {
      books.forEach((b) => {
        const id = b?.id ?? b?.bookID;
        if (id && Number.isInteger(Number(b?.slot))) updateDocument(id, { slot: Number(b.slot) });
      });
      setEditMode(false);
    });
    return () => {
      setUnityBookClickedHandler(null);
      setUnityBookAddedHandler(null);
      setUnityBookAddFailedHandler(null);
      setUnityBooksReorderedHandler(null);
    };
  }, [openBook, updateDocument]);

  const openBookIds = useMemo(() => {
    const ids = new Set(deskDocIds.map(bookIdForDeskDoc).filter(Boolean));
    return shelfDocs
      .filter((d) => ids.has(d.id))
      .map((d) => d.id)
      .join(",");
  }, [shelfDocs, deskDocIds]);
  useEffect(() => {
    if (!loaded) return;
    const open = openBookIds ? openBookIds.split(",") : [];
    if (process.env.NODE_ENV === "development") {
      console.info("[Bookshelf] open books", open, "from desk doc ids", deskDocIds);
    }
    sendOpenBooks(open);
  }, [loaded, openBookIds]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => { if (editModeRef.current) sendBookEditMode(false); }, []);

  useEffect(() => {
    if (process.env.NODE_ENV !== "development") return;
    window.__nqOpenBook = openBook;
    return () => { delete window.__nqOpenBook; };
  }, [openBook]);

  const toggleCategory = (id) =>
    setActiveCategories((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]));

  useEffect(() => {
    const timer = setTimeout(() => setSubmittedQuery(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const runSearch = () => setSubmittedQuery(query.trim());

  const clearFilters = () => {
    setActiveCategories([]);
    setQuery("");
    setSubmittedQuery("");
  };

  // ── Actions ────────────────────────────────────────────────
  const handleUpload = async (files) => {
    if (!files?.length) return;
    if (!roomId || !userId) { setBanner("Can't upload right now — no active room."); return; }
    setUploading(true);
    setBanner("");
    const failed = [];
    for (const f of files) {
      try {
        await uploadFileToRoom(f, { userId, roomId });
      } catch (e) {
        console.error("Bookshelf upload error:", f?.name, e);
        failed.push(e?.message || `Couldn't upload ${f?.name || "file"}.`);
      }
    }
    setUploading(false);
    if (failed.length) setBanner(failed.join(" "));
  };

  const handleDownload = async (doc) => {
    if (!doc) return;
    if (doc.storagePath) {
      try {
        const fileName = doc.fileName || doc.title || "file";
        const url = await getRoomFileUrl(doc.storagePath, 3600, { download: fileName });
        if (!url) throw new Error("No signed URL returned");
        const a = document.createElement("a");
        a.href = url; a.download = fileName; a.rel = "noopener noreferrer";
        document.body.appendChild(a); a.click(); a.remove();
      } catch (e) {
        console.error("Bookshelf download error:", e);
        setBanner("Couldn't download that file. Try again.");
      }
      return;
    }
    const text = toPlainText(doc.content);
    if (!text) return;
    const url = URL.createObjectURL(new Blob([text], { type: "text/plain;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url; a.download = `${doc.title || "document"}.txt`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const handleArchive = (doc) => {
    const archiving = doc.status !== "archived";
    updateDocument(doc.id, { status: archiving ? "archived" : "final" });
    if (archiving) sendRemoveBook(doc.id);
    else sendAddBook({ ...doc, status: "final" });
    setSelectedId(null);
    setPreviewId(null);

    if (archiving && unplacedRef.current.size) {
      const retry = [...unplacedRef.current];
      unplacedRef.current.clear();
      retry.forEach((id) => {
        const pending = roomDocsRef.current.find((d) => d.id === id);
        if (pending) sendAddBook(pending);
      });
      setBanner("");
    }
  };

  const handleDelete = (doc) => {
    deleteDocument(doc.id);
    sendRemoveBook(doc.id);
    if (onDeskDocDeleted) onDeskDocDeleted(deskDocIdForBook(doc.id));
    setSelectedId(null);
    setPreviewId(null);
  };

  const handleToggleReference = (doc) => {
    if (doc.type === "reference") {
      updateDocument(doc.id, { type: doc.priorType || "document" });
    } else {
      updateDocument(doc.id, { type: "reference", priorType: doc.type });
    }
  };

  const sendTargets = (doc) => {
    const hasText = !!toPlainText(doc.content);
    return [
      { id: "desk", label: "Desk", enabled: hasText,
        reason: hasText ? "" : "Files open at the Desk as attachments — not built yet." },
      { id: "whiteboard", label: "Whiteboard", enabled: hasText,
        reason: hasText ? "" : "Files pin to the Whiteboard as tiles — not built yet." },
      { id: "calendar", label: "Calendar", enabled: false,
        reason: "Attaching to a calendar entry isn't built yet." },
    ];
  };

  const handleSend = (doc, target) => {
    onOpenInHotspot?.(target, doc);
    setSelectedId(null);
    setPreviewId(null);
  };

  const actionsFor = (doc) => ({
    targets: sendTargets(doc),
    onSend: (target) => handleSend(doc, target),
    onArchive: () => handleArchive(doc),
    onDownload: () => handleDownload(doc),
    onDelete: () => handleDelete(doc),
  });

  return (
    <div className="fixed inset-0 pointer-events-none font-['DM_Sans']" style={{ zIndex: 55 }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />

      <div className="absolute left-0 right-0" style={{ top: FRAME_TOP }}>
      <div className="mx-auto w-full px-4 pointer-events-auto" style={{ maxWidth: FRAME_W }}>
        <div
          className="flex items-center justify-between gap-[13px] px-[18px] py-[14px] rounded-[12px]"
          style={HEADER_GLASS}
        >
          <div className="flex items-center gap-[11px] min-w-0" style={{ color: "#FFF" }}>
            <Icon name="document" size={16} />
            <span className="text-[15px] font-semibold" style={{ color: "#FFF" }}>Bookshelf</span>
            <span className="text-[13px]" style={{ color: "#FFF" }}>
              {filtering
                ? `${visibleDocs.length} of ${sourceDocs.length} docs`
                : `${sourceDocs.length} doc${sourceDocs.length === 1 ? "" : "s"}`}
            </span>
            {filtering && (
              <button
                type="button" onClick={clearFilters}
                className="text-[12px] font-semibold bg-transparent border-0 cursor-pointer"
                style={{ color: "#FFF" }}
              >
                Clear
              </button>
            )}
          </div>
          <div className="flex items-center gap-[15px] flex-shrink-0">
            <button
              type="button"
              onClick={() => {
                const next = !editMode;
                setEditMode(next);
                sendBookEditMode(next);
                if (next) { setSelectedId(null); setPreviewId(null); }
              }}
              className="text-[13px] font-medium bg-transparent border-0 cursor-pointer"
              style={{ color: "#FFF", opacity: editMode ? 1 : 0.75 }}
            >
              {editMode ? "Done" : "Edit"}
            </button>
            <button
              type="button" disabled={uploading} onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-[6px] text-[13px] font-medium bg-transparent border-0"
              style={{ color: "#FFF", cursor: uploading ? "default" : "pointer", opacity: uploading ? 0.6 : 1 }}
            >
              {uploading ? "Uploading…" : (<><span className="text-[15px] leading-none font-normal">+</span>Add</>)}
            </button>
            <input
              ref={fileInputRef} type="file" multiple className="hidden" disabled={uploading}
              onChange={(e) => { handleUpload(Array.from(e.target.files)); e.target.value = ""; }}
            />
            <button
              type="button" onClick={onClose} aria-label="Close bookshelf"
              className="text-[15px] bg-transparent border-0 cursor-pointer leading-none"
              style={{ color: "#FFF" }}
            >
              ✕
            </button>
          </div>
        </div>
      </div>

      {/* Category tabs */}
      <div className="px-4" style={{ marginTop: HEADER_GAP }}>
        <CategoryTabs
          active={activeCategories}
          onToggle={toggleCategory}
          showArchived={showArchived}
          onToggleArchived={() => { setShowArchived((v) => !v); setSelectedId(null); setPreviewId(null); }}
          archivedCount={archivedDocs.length}
        />
      </div>

      {/* Errors — flows under the tab row rather than floating over the shelf. */}
      {(banner || storageError) && (
        <div
          className="mx-auto mt-[14px] pointer-events-auto flex items-center gap-2 px-4 py-2 rounded-[10px] text-[12px] font-medium w-fit max-w-[520px]"
          style={{ background: "#FDECEA", color: "#B3261E", boxShadow: "0 3px 14px rgba(0,0,0,0.16)" }}
        >
          <span>⚠</span>
          <span className="flex-1">{banner || "Couldn't sync your Bookshelf. Changes may not be saved."}</span>
          {banner && (
            <button
              type="button" onClick={() => setBanner("")}
              className="bg-transparent border-0 cursor-pointer" style={{ color: "#B3261E" }}
            >
              ✕
            </button>
          )}
        </div>
      )}
      </div>

      {/* Nothing matched */}
      {filtering && visibleDocs.length === 0 && (
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-auto px-5 py-4 rounded-[12px] text-[13px] text-center"
          style={{ ...GLASS, color: INK_MUTED }}
        >
          Nothing on this shelf matches.
          <button
            type="button" onClick={clearFilters}
            className="ml-2 font-semibold bg-transparent border-0 cursor-pointer"
            style={{ color: TEAL }}
          >
            Clear filters
          </button>
        </div>
      )}

      {/* Search */}
      <div
        className="absolute left-1/2 -translate-x-1/2 w-full px-4 pointer-events-auto"
        style={{ bottom: Math.max(SEARCH_BOTTOM, bottomInset + 12), maxWidth: SEARCH_W }}
      >
        <div
          className="flex items-center gap-[11px] pl-[16px] pr-[6px] py-[6px] rounded-[12px]"
          style={GLASS}
        >
          <span style={{ color: TEAL }}>✦</span>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") runSearch(); }}
            placeholder="Search titles or content..."
            className="flex-1 bg-transparent border-0 outline-none text-[13px]"
            style={{ color: INK }}
          />
          <button
            type="button" onClick={runSearch}
            className="px-[17px] py-[7px] rounded-[9px] text-[12px] font-semibold border-0 cursor-pointer"
            style={{ background: query.trim() ? TEAL : "rgba(0,0,0,0.07)", color: query.trim() ? "#FFF" : INK_MUTED }}
          >
            Find
          </button>
        </div>
      </div>

      {/* Click card */}
      {selectedDoc && clickPoint && !previewDoc && (
        <BookCard
          doc={selectedDoc}
          point={clickPoint}
          rightInset={rightInset}
          onClose={() => setSelectedId(null)}
          onPreview={() => setPreviewId(selectedDoc.id)}
          onToggleReference={() => handleToggleReference(selectedDoc)}
          {...actionsFor(selectedDoc)}
        />
      )}

      {/* Full document */}
      {previewDoc && (
        <DocumentModal
          doc={previewDoc}
          onClose={() => setPreviewId(null)}
          {...actionsFor(previewDoc)}
        />
      )}
    </div>
  );
}
